<script lang="ts">
  import {
    FDI_SUP_DER, FDI_SUP_IZQ, FDI_INF_DER, FDI_INF_IZQ,
    ESTADO_COLOR, ESTADO_LABEL,
    contarPorEstado, fetchOdontograma, setPiezaEstadoAsync,
    type EstadoPieza, type OdontogramaPaciente,
  } from '../lib/odontograma';
  import { session } from '../lib/auth.svelte';

  let {
    pacienteId,
    compact = false,
  }: {
    pacienteId: string;
    compact?: boolean;
  } = $props();

  let odonto = $state<OdontogramaPaciente>({ pacienteId: '', piezas: {}, historial: [] });
  let pintar = $state<EstadoPieza>('caries');
  let cargando = $state(false);
  let guardando = $state(false);

  const autor = $derived(session.value?.usuario ?? 'Odontólogo');

  const estadosPintar: EstadoPieza[] = [
    'caries', 'restaurado', 'corona', 'endodoncia', 'ausente', 'extraccion', 'implante', 'sano',
  ];

  async function recargar() {
    if (!pacienteId) return;
    cargando = true;
    try {
      odonto = await fetchOdontograma(pacienteId);
    } finally {
      cargando = false;
    }
  }

  function estadoPieza(n: number): EstadoPieza {
    return odonto.piezas[n] ?? 'sano';
  }

  async function aplicar(n: number) {
    if (!pacienteId || guardando) return;
    guardando = true;
    try {
      odonto = await setPiezaEstadoAsync(pacienteId, n, pintar, autor);
    } finally {
      guardando = false;
    }
  }

  $effect(() => { pacienteId; void recargar(); });
</script>

<section class="odontograma" class:compact>
  {#if !compact}
    <p class="hint">Click en cada pieza para marcar estado. Notación FDI · 32 dientes permanentes.</p>
  {/if}

  <div class="paleta">
    <span class="paleta-label">Marcar como:</span>
    <div class="paleta-btns">
      {#each estadosPintar as e}
        <button
          type="button"
          class="chip"
          class:active={pintar === e}
          onclick={() => pintar = e}
          title={ESTADO_LABEL[e]}
        >
          <span class="swatch" style="background:{ESTADO_COLOR[e]}"></span>
          {ESTADO_LABEL[e]}
        </button>
      {/each}
    </div>
  </div>

  <div class="chart">
    <div class="arc-label">Superior · derecha — izquierda</div>
    <div class="arc">
      {#each FDI_SUP_DER as n}
        <button type="button" class="pieza" onclick={() => aplicar(n)} title={`Pieza ${n}: ${ESTADO_LABEL[estadoPieza(n)]}`}>
          <span class="num">{n}</span>
          <svg width="28" height="36" viewBox="0 0 28 36" aria-hidden="true">
            <rect x="2" y="2" width="24" height="32" rx="6" stroke="#475569" stroke-width="1.5" fill={ESTADO_COLOR[estadoPieza(n)]} />
            {#if estadoPieza(n) === 'ausente'}
              <line x1="4" y1="4" x2="24" y2="32" stroke="#dc2626" stroke-width="2" />
            {:else if estadoPieza(n) === 'extraccion'}
              <text x="14" y="22" text-anchor="middle" font-size="14" fill="white" font-weight="bold">X</text>
            {:else if estadoPieza(n) === 'endodoncia'}
              <line x1="14" y1="6" x2="14" y2="30" stroke="white" stroke-width="2" />
            {:else if estadoPieza(n) === 'corona'}
              <circle cx="14" cy="18" r="6" fill="none" stroke="white" stroke-width="2" />
            {/if}
          </svg>
        </button>
      {/each}
      <div class="mid"></div>
      {#each FDI_SUP_IZQ as n}
        <button type="button" class="pieza" onclick={() => aplicar(n)} title={`Pieza ${n}: ${ESTADO_LABEL[estadoPieza(n)]}`}>
          <span class="num">{n}</span>
          <svg width="28" height="36" viewBox="0 0 28 36" aria-hidden="true">
            <rect x="2" y="2" width="24" height="32" rx="6" stroke="#475569" stroke-width="1.5" fill={ESTADO_COLOR[estadoPieza(n)]} />
            {#if estadoPieza(n) === 'ausente'}
              <line x1="4" y1="4" x2="24" y2="32" stroke="#dc2626" stroke-width="2" />
            {:else if estadoPieza(n) === 'extraccion'}
              <text x="14" y="22" text-anchor="middle" font-size="14" fill="white" font-weight="bold">X</text>
            {:else if estadoPieza(n) === 'endodoncia'}
              <line x1="14" y1="6" x2="14" y2="30" stroke="white" stroke-width="2" />
            {:else if estadoPieza(n) === 'corona'}
              <circle cx="14" cy="18" r="6" fill="none" stroke="white" stroke-width="2" />
            {/if}
          </svg>
        </button>
      {/each}
    </div>

    <div class="divider"></div>

    <div class="arc arc-flip">
      {#each FDI_INF_DER as n}
        <button type="button" class="pieza flip" onclick={() => aplicar(n)} title={`Pieza ${n}: ${ESTADO_LABEL[estadoPieza(n)]}`}>
          <svg width="28" height="36" viewBox="0 0 28 36" aria-hidden="true">
            <rect x="2" y="2" width="24" height="32" rx="6" stroke="#475569" stroke-width="1.5" fill={ESTADO_COLOR[estadoPieza(n)]} />
            {#if estadoPieza(n) === 'ausente'}
              <line x1="4" y1="4" x2="24" y2="32" stroke="#dc2626" stroke-width="2" />
            {:else if estadoPieza(n) === 'extraccion'}
              <text x="14" y="22" text-anchor="middle" font-size="14" fill="white" font-weight="bold">X</text>
            {:else if estadoPieza(n) === 'endodoncia'}
              <line x1="14" y1="6" x2="14" y2="30" stroke="white" stroke-width="2" />
            {:else if estadoPieza(n) === 'corona'}
              <circle cx="14" cy="18" r="6" fill="none" stroke="white" stroke-width="2" />
            {/if}
          </svg>
          <span class="num">{n}</span>
        </button>
      {/each}
      <div class="mid"></div>
      {#each FDI_INF_IZQ as n}
        <button type="button" class="pieza flip" onclick={() => aplicar(n)} title={`Pieza ${n}: ${ESTADO_LABEL[estadoPieza(n)]}`}>
          <svg width="28" height="36" viewBox="0 0 28 36" aria-hidden="true">
            <rect x="2" y="2" width="24" height="32" rx="6" stroke="#475569" stroke-width="1.5" fill={ESTADO_COLOR[estadoPieza(n)]} />
            {#if estadoPieza(n) === 'ausente'}
              <line x1="4" y1="4" x2="24" y2="32" stroke="#dc2626" stroke-width="2" />
            {:else if estadoPieza(n) === 'extraccion'}
              <text x="14" y="22" text-anchor="middle" font-size="14" fill="white" font-weight="bold">X</text>
            {:else if estadoPieza(n) === 'endodoncia'}
              <line x1="14" y1="6" x2="14" y2="30" stroke="white" stroke-width="2" />
            {:else if estadoPieza(n) === 'corona'}
              <circle cx="14" cy="18" r="6" fill="none" stroke="white" stroke-width="2" />
            {/if}
          </svg>
          <span class="num">{n}</span>
        </button>
      {/each}
    </div>
    <div class="arc-label">Inferior · derecha — izquierda</div>
  </div>

  {#if !compact}
    <div class="footer-grid">
      <div class="resumen">
        <strong>Resumen</strong>
        {#each ['caries', 'restaurado', 'corona', 'endodoncia', 'ausente'] as e}
          {@const est = e as EstadoPieza}
          <div class="res-row">
            <span><span class="swatch sm" style="background:{ESTADO_COLOR[est]}"></span>{ESTADO_LABEL[est]}</span>
            <span class="cnt">{contarPorEstado(odonto.piezas, est)}</span>
          </div>
        {/each}
      </div>
      {#if odonto.historial.length}
        <div class="hist">
          <strong>Historial reciente</strong>
          <ul>
            {#each odonto.historial.slice(0, 6) as h}
              <li><time>{h.fecha}</time> · pieza <strong>{h.pieza}</strong> → {ESTADO_LABEL[h.estado]}</li>
            {/each}
          </ul>
        </div>
      {/if}
    </div>
  {/if}
</section>

<style>
  .odontograma { margin-top: 0.5rem; }
  .hint { color: #5b7480; font-size: 0.88rem; margin: 0 0 0.75rem; }
  .paleta { margin-bottom: 0.75rem; }
  .paleta-label { display: block; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; color: #64748b; margin-bottom: 0.35rem; }
  .paleta-btns { display: flex; flex-wrap: wrap; gap: 0.35rem; }
  .chip {
    display: inline-flex; align-items: center; gap: 0.3rem;
    border: 1px solid #cbd5e1; background: #fff; border-radius: 999px;
    padding: 0.25rem 0.55rem; font-size: 0.72rem; cursor: pointer;
  }
  .chip.active { border-color: #0f766e; background: #ecfdf5; font-weight: 700; }
  .swatch { width: 12px; height: 12px; border-radius: 3px; border: 1px solid #94a3b8; flex-shrink: 0; }
  .swatch.sm { width: 10px; height: 10px; }
  .chart {
    background: #fff; border: 1px solid #d6e7ea; border-radius: 12px;
    padding: 0.85rem; overflow-x: auto;
  }
  .arc-label { text-align: center; font-size: 0.68rem; color: #64748b; margin: 0.15rem 0; }
  .arc { display: flex; justify-content: center; align-items: flex-end; gap: 2px; min-width: min-content; }
  .arc-flip { align-items: flex-start; }
  .mid { width: 4px; background: #cbd5e1; margin: 0 2px; align-self: stretch; border-radius: 2px; }
  .divider { height: 1px; background: #cbd5e1; margin: 0.5rem 0; }
  .pieza {
    border: 0; background: none; padding: 0; cursor: pointer;
    display: flex; flex-direction: column; align-items: center; gap: 1px;
  }
  .pieza:hover svg { transform: scale(1.08); }
  .pieza svg { transition: transform 0.12s; }
  .num { font-size: 0.58rem; color: #64748b; font-weight: 700; font-variant-numeric: tabular-nums; }
  .compact .paleta-btns { display: none; }
  .compact .chart { padding: 0.5rem; }
  .footer-grid {
    display: grid; grid-template-columns: 1fr 1.4fr; gap: 0.75rem; margin-top: 0.75rem;
  }
  @media (max-width: 640px) { .footer-grid { grid-template-columns: 1fr; } }
  .resumen, .hist {
    background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 0.65rem 0.75rem; font-size: 0.82rem;
  }
  .resumen strong, .hist strong { display: block; margin-bottom: 0.4rem; color: #0b2e36; }
  .res-row { display: flex; justify-content: space-between; align-items: center; padding: 0.15rem 0; }
  .cnt { font-weight: 700; font-variant-numeric: tabular-nums; }
  .hist ul { list-style: none; margin: 0; padding: 0; }
  .hist li { padding: 0.2rem 0; border-bottom: 1px solid #f1f5f9; color: #444; }
  .hist time { color: #64748b; font-size: 0.78rem; }
</style>
